import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/** Merge Tailwind classes intelligently, deduping conflicts. */
export function cn(...inputs: ClassValue[]): string {
    return twMerge(clsx(inputs));
}

/**
 * Is this a mass-created appointment with NO PROMISED TIME (#66)?
 *
 * ⛔ THE ONE PLACE THAT ASKS. `schedulingMode` is optional on `AppointmentDto`
 * and is `undefined` on every payload today, because the calendar query does not
 * select `SchedulingMode` yet (#127 — adding it would make the most-read endpoint
 * in the app depend on migration 150). So a bare `a.schedulingMode === 'DayOnly'`
 * is correct-but-fragile and a bare `!== 'TimeSlot'` is outright wrong: it would
 * call every ordinary appointment day-only the moment somebody wrote it.
 *
 * Absent means TimeSlot. One function, so there is one thing to change when #127
 * lands rather than a search for every comparison.
 */
/*
 * ---- ⭐ THE PARAMETER WIDENED TO `string | null` (2026-09-27) ---------------
 *
 * ⛔ BECAUSE A CALLER APPEARED THAT GENUINELY HAS A STRING. The orders panel's
 * `bookedOn[]` carries `schedulingMode: string | null` straight off
 * `dock.Appointment`, and the narrow union forced the call site to write
 * `b.schedulingMode === 'DayOnly' ? … : …` before it could ask — which is the
 * comparison this function exists so that nobody writes.
 *   ** A TYPE THAT MAKES CALLERS RE-IMPLEMENT THE FUNCTION TO SATISFY IT HAS
 *      DEFEATED THE FUNCTION. **
 *
 * ⚠️ WHAT IT COSTS: `schedulingMode: 'TimeSlott'` no longer fails to compile at a
 * CALL SITE. ⭐ It is a fair trade — the only comparison is the one line below,
 * every existing caller still type-checks unchanged, and the sentence the
 * docstring above promises ("absent means TimeSlot") is unaffected.
 */
export function isDayOnly(a: { schedulingMode?: string | null } | null | undefined): boolean {
    return a?.schedulingMode === 'DayOnly';
}

/**
 * ⭐ DOES THIS APPOINTMENT NOW HAVE A REAL, KNOWN SPAN? (added 2026-08-24)
 *
 * ⛔ A DAY-ONLY TRUCK THAT HAS BEEN AND GONE IS NOT TIMELESS ANY MORE. It was
 * booked without a time, so it packs into a chip marked *"No time assigned"* — and
 * that marker stays factually correct right up until somebody records when the
 * truck actually arrived and left, at which point the screen is asserting we do not
 * know something we plainly do.
 *
 * Pete, 2026-08-24, having marked one departed with real times: *"gave it the
 * Blocked status Icon not the Departed Status Icon."* ⚠️ It was not the blocked
 * icon — it was the day-only no-time marker, which by then was a lie. The dashed
 * outline, the hatch and the struck-through clock all say "this has no time", and
 * all three were wrong.
 *
 * ⛔ BOTH ENDS ARE REQUIRED. An arrival with no departure is a truck still on site;
 * drawing it to "now" would make the block grow every time the grid refreshed, which
 * is a moving target rather than a fact. It stays a chip until it leaves.
 *
 * ⚠️ ONLY DAY-ONLY APPOINTMENTS ARE REPOSITIONED BY THIS. A booked timeslot keeps
 * its SCHEDULED position even when the truck ran late — the board is a plan, and
 * silently redrawing plans as outcomes would hide every late arrival it exists to
 * show.
 */
export function hasActualSpan(
    a: { actualArrivalUtc?: string | null; actualDepartureUtc?: string | null } | null | undefined,
): boolean {
    return !!a?.actualArrivalUtc && !!a?.actualDepartureUtc;
}

/**
 * What to show where a start time would go.
 *
 * ⭐ "All day" IS THE HONEST ANSWER for a day-only appointment. Its stored window
 * spans the facility's operating hours so that capacity and daily-limit queries
 * can see it — but the scheduler agreed to a DAY, and rendering "05:00" puts a
 * promise on screen that nobody made. The first driver to be told 05:00 because
 * of it would be entirely justified in turning up at 05:00.
 */
export function appointmentTimeLabel(
    a: { schedulingMode?: string | null } | null | undefined,
    formatted: string,
): string {
    return isDayOnly(a) ? 'All day' : formatted;
}

/**
 * ---- ⭐⭐ A UTC INSTANT AS A CLOCK TIME — **ONE** DEFINITION (2026-09-27) -----
 *
 * `'2026-09-27T15:00:00Z'` → `'9:00 AM'`, in the reader's locale and zone.
 *
 * ⛔⛔ IT LIVES HERE BECAUSE FOUR COPIES ALREADY EXIST AND THEY HAVE ALREADY
 * DRIFTED. `CheckInPanel`, `LabourPanel`, `HistoryPanel` and `CommunicationsPanel`
 * each define a private `hhmm`, and they are NOT the same function: History's
 * returns `''` on an unparseable instant, Labour's returns the string
 * `"Invalid Date"` and puts it on screen. The Orders panel needed a fifth, which
 * is the moment to stop.
 *   ** A HELPER COPIED FOUR TIMES IS NOT A CONVENTION, IT IS FOUR FUNCTIONS THAT
 *      HAPPEN TO SHARE A NAME UNTIL ONE OF THEM IS FIXED. **
 *
 * ⭐ IT TAKES THE STRICTER BEHAVIOUR — `''` on a bad value, never `"Invalid
 * Date"`. A blank says "we have nothing here", which is true; `"Invalid Date"`
 * says the software is broken, to a scheduler who cannot act on either.
 *
 * ⚠️ IT DOES **NOT** ANSWER THE DAY-ONLY QUESTION — `appointmentTimeLabel` above
 * does, and the two compose: `appointmentTimeLabel(a, hhmm(a.scheduledStartUtc))`.
 * ⛔ Keeping them separate is deliberate: this one has no appointment to ask.
 *
 * ⬜ THE OTHER FOUR ARE NOT CHANGED BY THIS. Four call-site swaps in four panels
 * is a refactor with its own risk and no behaviour Pete asked for; it is an open
 * item, and this is where they should land when it happens.
 */
export function hhmm(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? ''
        : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/**
 * ---- ⭐⭐ HOW OLD IS THIS CACHED ROW? — **ONE** DEFINITION (moved 2026-09-27) ---
 *
 * `'synced 9d ago'`, `'synced 6h ago'`, or `null` when it is fresh enough not to
 * mention.
 *
 * ⛔ MOVED HERE FROM `AppointmentSearch.tsx`, where it was private, the moment a
 * second caller appeared. Pete asked for the sync time on the order detail:
 * *"having a recorded updated field on the detail would be key so we know when the
 * orders was pulled last from the sync"* — and copying nine lines would have started
 * the `hhmm` story over, which is open item 51 and was opened THIS MORNING.
 *   ** THE SECOND CALLER IS THE MOMENT. A HELPER COPIED ONCE IS COPIED AGAIN. **
 *
 * ⭐ THE TWO-HOUR FLOOR IS THE JUDGEMENT AND IT IS DELIBERATE. A sync that ran an
 * hour ago is working; saying so on every row would make the words disappear, and the
 * whole value of this string is that seeing it at all means something is wrong.
 *   ** A STALENESS WARNING SHOWN WHEN NOTHING IS STALE SPENDS THE ATTENTION THE REAL
 *      ONE NEEDS. **
 *
 * ⚠️ `null` FOR A FUTURE TIMESTAMP (`ms < 0`) rather than "0h ago". A cache row dated
 * ahead of now means clock skew between the sync host and this browser, and "synced
 * -3h ago" is the kind of output that makes a reader distrust the whole panel.
 *
 * ⛔ IT ANSWERS "IS THIS WORTH MENTIONING", NOT "WHEN WAS IT". A screen that must
 * always state the time — a detail pane — shows the timestamp itself and uses this
 * only for the nudge beside it. Returning null is not the same as having no data.
 */
export function staleness(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const ms = Date.now() - new Date(iso).getTime();
    if (!Number.isFinite(ms) || ms < 0) return null;
    const hours = Math.floor(ms / 3_600_000);
    if (hours < 2) return null;
    if (hours < 48) return `synced ${hours}h ago`;
    return `synced ${Math.floor(hours / 24)}d ago`;
}

/**
 * `'p:1,x:0.620,y:0.880'` → the three numbers, or null if it is anything else.
 *
 * ⛔ ONE DEFINITION. This lived as a private function inside SignDocumentDialog,
 * and #146 needed the same parse to decide whether a load can be signed without a
 * human dragging the box. A second copy of a format parser is how the two come to
 * disagree about what a stored placement means — and the value it decodes decides
 * where ink lands on a carrier's document.
 *
 * ⚠️ RETURNS null RATHER THAN A DEFAULT. A malformed placement must not silently
 * become "bottom right": the caller has to decide whether stamping blind is
 * acceptable, and for the automatic path in #146 it is not.
 */
export function parsePlacement(
    raw: string | null | undefined,
): { page: number; fx: number; fy: number } | null {
    const m = /^p:(\d+),x:([\d.]+),y:([\d.]+)$/.exec((raw ?? '').trim());
    if (!m) return null;
    const page = Number(m[1]);
    const fx = Number(m[2]);
    const fy = Number(m[3]);
    return Number.isFinite(page) && Number.isFinite(fx) && Number.isFinite(fy)
        ? { page, fx, fy } : null;
}

/**
 * ---- ⛔ THE SENTINEL FOR "NOTHING CHOSEN" IN A RADIX SELECT ----------------
 *
 * ⚠️ WRITTEN AFTER SHIPPING THE BUG IT PREVENTS. On 2026-08-25 a "Not known yet (TBD)"
 * option was added to the carrier pickers as `<SelectItem value="">`, and Radix throws
 * on an empty-string item value:
 *
 *     A <Select.Item /> must have a value prop that is not an empty string.
 *
 * ⛔ IT THROWS DURING RENDER, so the dialog does not degrade — it takes the page down.
 * Pete opened Edit from an appointment and got a white screen. Nothing in the message
 * names the field, the file or the option, which is why it read as an unrelated
 * sign-in failure and cost a wrong diagnosis before the console was read.
 *
 * ⭐ RADIX IS RIGHT AND THE OPTION IS TOO. The empty string is how a Select expresses
 * "no selection, show the placeholder", so it cannot also be a selectable value — the
 * two meanings would be indistinguishable. A sentinel keeps both.
 *
 * ⛔ IT MUST NEVER REACH THE SERVER. Translate at the boundary, both ways:
 *
 *     <Select value={carrierCode || NO_SELECTION}
 *             onValueChange={(v) => setCarrierCode(v === NO_SELECTION ? '' : v)}>
 *       <SelectItem value={NO_SELECTION}>Not known yet (TBD)</SelectItem>
 *
 * ⚠️ The underscores and the improbable word are deliberate: this has to be a value no
 * real carrier or client code could ever collide with.
 */
export const NO_SELECTION = '__symbia_no_selection__';

/**
 * ---- ⭐ THE ARRIVALS COLUMN'S SECTION HEADERS, IN ONE PLACE ----------------
 *
 * Pete, 2026-08-25, looking at the column with three panels stacked in it:
 * *"This is getting a little messy. Can you make the titles (Driver Check in,
 * Scanned Paperwork, Labor) have like a light background color or something so you
 * can distinguish between card areas?"*
 *
 * ⛔ AND THE REAL FAULT WAS THAT THEY DID NOT AGREE. "Driver Check-Ins" already had a
 * tinted bar; "Scanned Paperwork" and "Labor" only tinted on HOVER. So one header
 * looked like a header and two looked like rows — which is precisely why the column
 * read as a run-on list instead of three cards.
 *
 * ⚠️ SO IT IS A SHARED CONSTANT AND NOT THREE COPIES OF A CLASS STRING. Three panels
 * that must look identical, in three files, is exactly the shape that drifts: someone
 * tunes one of them and the column is subtly mismatched again with nothing to catch it.
 */
/**
 * ---- ⭐ DARKER FRAME LINES (Decision 269, 2026-09-28) ----------------------
 *
 * > Pete: *"Its a little hard when all these panels are full with information, to
 * > distinquish between the panels or the sections within the panels"* → *"maybe a
 * > darker border around those might look better"* → *"Lets do number 2 [darker
 * > border only] but it will have to be around the boarder of the calendar too and
 * > its one header"* → *"Don't forget week and month views"*.
 *
 * ⭐ BORDERS ONLY; THE GREYS ARE UNCHANGED. One step darker than the app's `--border`
 * (stone-200 → stone-400; Pete picked the darker of two previews), and ONLY on the frame: panel outlines, the calendar's
 * outline (Day, Week, Month), the calendar's header strip, panel headers and the
 * subsection bars. ⛔ Not the global `--border` token — that would darken every
 * card INSIDE a panel too and the frame would stand out no more than before.
 *   ** SEPARATION IS CONTRAST BETWEEN THE FRAME AND WHAT IS IN IT, NOT DARKNESS. **
 *
 * ⚠️ THE LINE ABOVE A HEADER IS A 1px SHADOW, NOT `border-t`. Panels stack in one
 * column, so each header needs a line above it — but the first header sits on the
 * column's own outline, where a border-t would draw a 2px double line. The shadow
 * falls outside the element, so the first one is clipped by the column's
 * `overflow-hidden` and only the stacked ones show.
 */
export const FRAME_BORDER = 'border-stone-400';
/**
 * ⭐ THE APP'S WARNING TONE, AS A CHIP (F106, 2026-09-29). Pete: *"make these the
 * same coloring as the warnings though because that is basically what these …
 * bubbles are"*. The palette is `WARN_BOX`'s in CheckInPanel — amber-300 border,
 * amber-50 fill, amber-800 text — so a warning looks like a warning wherever it is.
 * ⛔ The three open-item chips (no-shows, no-departures, No WMS) all use this.
 */
export const WARNING_CHIP = 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100';
const FRAME_LINE_ABOVE = 'shadow-[0_-1px_0_0_#a8a29e]';   /* stone-400 */
/** The Inbound / Outbound bars inside a panel. */
export const SUBSECTION_HEADER = `border-b ${FRAME_BORDER} ${FRAME_LINE_ABOVE}`;
export const SECTION_HEADER = `bg-muted/70 border-b ${FRAME_BORDER} ${FRAME_LINE_ABOVE}`;
